import { useEffect, useState, useRef } from "react";
import { Box, Button, Card, CardContent, CircularProgress, Collapse, Container, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { PlansChoiceF } from "./PlansChoiceF";
import { APP_VERSION } from "../../utils/versionChecker";
import { PlansChoicePPB } from "./PlansChoicePPB";
import { PlansChoicePPBF } from "./PlansChoicePPBF";
export const PlansFire = () => {
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [openCards, setOpenCards] = useState({});
	const dienstAnchorRef = useRef(null);

	const parseDate = (value) => {
		const parsed = new Date(value);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	};

	const normalizeDate = (value) => {
		const parsed = parseDate(value);
		if (!parsed) return null;
		parsed.setHours(0, 0, 0, 0);
		return parsed;
	};

	const formatDate = (value) => {
		const parsed = parseDate(value);
		if (!parsed) return value;
		const day = String(parsed.getDate()).padStart(2, "0");
		const month = String(parsed.getMonth() + 1).padStart(2, "0");
		const year = String(parsed.getFullYear());
		return `${day}-${month}-${year}`;
	};

	const getPdfHref = (value) => {
		if (!value) return "";
		if (/^https?:\/\//i.test(value)) {
			try {
				const parsed = new URL(value);
				const path = parsed.pathname.startsWith("/") ? parsed.pathname : `/${parsed.pathname}`;
				return `https://infogig.link${path.startsWith("/pdfs/") ? path : `/pdfs${path}`}`;
			} catch (error) {
				return value;
			}
		}
		const path = value.startsWith("/") ? value : `/${value}`;
		return `https://infogig.link${path.startsWith("/pdfs/") ? path : `/pdfs${path}`}`;
	};

	const loadRows = async () => {
		setLoading(true);
		setError("");

		try {
			const snapshot = await getDocs(collection(db, "Fahrplaene"));
			const entries = [];

			for (const docSnap of snapshot.docs) {
				const data = docSnap.data();
				const pdfSnap = await getDocs(collection(db, "Fahrplaene", docSnap.id, "pdfs"));
				const pdfs = pdfSnap.docs
					.map((pdfDoc, index) => ({
						id: pdfDoc.id,
						...pdfDoc.data(),
						order: pdfDoc.data().order ?? index + 1
					}))
					.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

				entries.push({
					id: docSnap.id,
					dateStart: data.dateStart || "",
					dateEnd: data.dateEnd || "",
					pdfs
				});
			}

			entries.sort((a, b) => {
				const aStart = parseDate(a.dateStart);
				const bStart = parseDate(b.dateStart);
				if (!aStart || !bStart) return 0;
				return aStart - bStart;
			});

			setRows(entries);
		} catch (loadError) {
			console.error("Failed to load Fahrplaene:", loadError);
			setError("Failed to load plans.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadRows();
	}, []);

	useEffect(() => {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		setOpenCards((prev) => {
			const next = { ...prev };
			rows.forEach((row) => {
				if (next[row.id] !== undefined) return;
				const start = normalizeDate(row.dateStart);
				const end = normalizeDate(row.dateEnd);
				const isActive = !!start && !!end && today >= start && today <= end;
				next[row.id] = isActive;
			});
			return next;
		});
	}, [rows]);

	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const ppbStart = new Date(2026, 3, 20);
	ppbStart.setHours(0, 0, 0, 0);

	const toggleOpen = (rowId) => {
		setOpenCards((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
	};

	const handleDienstClick = () => {
		if (dienstAnchorRef.current) {
			dienstAnchorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
		}
		window.setTimeout(() => {
			const input = document.getElementById("dienst-search-input");
			if (input) input.focus();
		}, 350);
	};

	return (
		<Container maxWidth="lg" sx={{ py: 3 }}>
			<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 2 }}>
				<Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
					<Typography variant="h5">Fahrpläne</Typography>
					<Typography variant="body2" color="text.secondary">
						v{APP_VERSION}
					</Typography>
				</Box>
				<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
					<Button
						variant="outlined"
						startIcon={<RefreshIcon />}
						onClick={loadRows}
						disabled={loading}
					>
						Refresh
					</Button>
					<Button variant="outlined" startIcon={<SearchIcon />} onClick={handleDienstClick}>
						Dienst
					</Button>
				</Box>
			</Box>

			{loading && (
				<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
					<CircularProgress />
				</Box>
			)}

			{!loading && error && (
				<Typography color="error" sx={{ mb: 2 }}>
					{error}
				</Typography>
			)}

			{!loading && !error && rows.length === 0 && (
				<Typography color="text.secondary">No plans available.</Typography>
			)}

			{!loading && !error && rows.length > 0 && (
				<>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
						{rows.map((row) => {
							const start = normalizeDate(row.dateStart);
							const end = normalizeDate(row.dateEnd);
							const isPast = !!end && end < today;
							if (isPast) return null;
							const isOpen = !!openCards[row.id];

							return (
								<Card key={row.id} sx={{ borderRadius: 2, boxShadow: 3, width: "100%" }}>
									<CardContent>
										<Box
											onClick={() => toggleOpen(row.id)}
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
												cursor: "pointer",
												userSelect: "none"
											}}
										>
											<Typography variant="h6">
												{formatDate(row.dateStart)} - {formatDate(row.dateEnd)}
											</Typography>
											<Typography variant="body2" color="text.secondary">
												{isOpen ? "▲" : "▼"}
											</Typography>
										</Box>

										<Collapse in={isOpen} timeout="auto" unmountOnExit>
											<Box
												sx={{
													display: "flex",
													flexWrap: "wrap",
													gap: 2,
													justifyContent: "flex-start",
													mt: 2
												}}
											>
												{row.pdfs.map((pdf, index) => (
													<Card
														key={pdf.id || index}
														component="a"
														href={getPdfHref(pdf.pdfUrl)}
														target="_blank"
														rel="noreferrer"
														sx={{
															minWidth: 100,
															maxWidth: 300,
															width: "fit-content",
															flex: "0 1 auto",
															boxShadow: 2,
															textDecoration: "none",
															color: "inherit"
														}}
													>
														<CardContent>
															{(pdf.isShortRange || pdf.shortDateStart || pdf.shortDateEnd) && (
																<Typography variant="body2" color="text.secondary">
																	{formatDate(pdf.shortDateStart)} - {formatDate(pdf.shortDateEnd)}
																</Typography>
															)}
															<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
																<Typography variant="body1" sx={{ fontWeight: 700, fontSize: 20 }}>
																	{(() => {
																		const raw = Array.isArray(pdf.dienstLinie)
																			? pdf.dienstLinie
																			: typeof pdf.dienstLinie === "string"
																				? pdf.dienstLinie.split(",")
																				: [];
																		const values = raw
																			.map((item) => String(item).replace(/\D/g, "").slice(0, 2))
																			.filter((item) => /^\d{2}$/.test(item));
																		return values.join(", ") || "(ohne DienstLinie)";
																	})()}
																</Typography>
																<PictureAsPdfIcon fontSize="small" color="action" />
															</Box>
															<Typography variant="body2" color="text.secondary">
																{pdf.description || ""}
															</Typography>
														</CardContent>
													</Card>
												))}
											</Box>
										</Collapse>
									</CardContent>
								</Card>
							);
						})}
					</Box>


					{/* {today < ppbStart ? (
						<Box sx={{ mt: 3 }}>
							<PlansChoiceF />
						</Box>
					) : (
						<>
							<PlansChoicePPB />	
						</>
					)} */}
{/* Anchor for Dienst search scroll */}
					<div ref={dienstAnchorRef} id="dienst-search-anchor" style={{ height: 0, margin: 0, padding: 0 }} />
					<PlansChoicePPBF />
			
				</>
			)}
		</Container>
	);
};
